import React from "react";
import { FaArrowLeft, FaThLarge, FaVideo, FaChartLine, FaUserCircle } from "react-icons/fa";

// YouTube-Studio-style shell for the teacher's marketplace/creator dashboard:
// a left sidebar (Dashboard / Content / Analytics / Profile) on desktop, a
// scrollable pill row on mobile, and a top bar. Each tab is its own panel so
// nothing is crammed onto one page.
const NAV = [
    ["dashboard", "Dashboard", FaThLarge, "/teacher/marketplace"],
    ["content", "Content", FaVideo, "/teacher/marketplace/content"],
    ["analytics", "Analytics", FaChartLine, "/teacher/marketplace/analytics"],
    ["profile", "Profile", FaUserCircle, "/teacher/marketplace/profile"],
];

export const StudioShell = ({ active, navigate, title, children }) => (
    <div className="min-h-screen bg-gray-50 font-montserrat">
        <header className="bg-white border-b border-gray-200 sticky top-0 z-30 flex items-center gap-3 px-4 h-14">
            <button onClick={() => navigate("/teacher-dashboard")} className="text-gray-500 hover:text-gray-800" aria-label="Back to dashboard">
                <FaArrowLeft size={18} />
            </button>
            <span className="font-bold text-gray-800">Creator Studio</span>
            <span className="hidden sm:inline text-gray-300">/</span>
            <span className="hidden sm:inline text-gray-500 text-sm">{title}</span>
        </header>

        <div className="flex">
            <aside className="hidden md:flex flex-col w-56 shrink-0 border-r border-gray-200 bg-white p-3 gap-1 sticky top-14 self-start h-[calc(100vh-3.5rem)]">
                {NAV.map(([key, label, Icon, path]) => (
                    <button
                        key={key}
                        onClick={() => navigate(path)}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${
                            active === key ? "bg-blue-50 text-blue-700" : "text-gray-600 hover:bg-gray-100"
                        }`}
                    >
                        <Icon size={16} /> {label}
                    </button>
                ))}
            </aside>

            <main className="flex-1 min-w-0">
                <nav className="md:hidden flex gap-2 overflow-x-auto no-scrollbar bg-white border-b border-gray-200 px-3 py-2 sticky top-14 z-20">
                    {NAV.map(([key, label, Icon, path]) => (
                        <button
                            key={key}
                            onClick={() => navigate(path)}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition ${
                                active === key ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600"
                            }`}
                        >
                            <Icon size={12} /> {label}
                        </button>
                    ))}
                </nav>
                <div className="max-w-5xl mx-auto p-4 sm:p-6">{children}</div>
            </main>
        </div>
    </div>
);

// Headline metric tile used across the Dashboard + Analytics panels.
export const StatCard = ({ icon: Icon, label, value, sub, accent = "text-blue-600" }) => (
    <div className="bg-white rounded-xl shadow-sm p-4 sm:p-5">
        <div className="flex items-center gap-2 text-gray-500 text-sm font-medium">
            {Icon && <Icon className={accent} />} {label}
        </div>
        <p className="text-2xl sm:text-3xl font-bold text-gray-800 mt-2">{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
);

// Card wrapper for a chart/section, with a title.
export const Panel = ({ title, action, children, className = "" }) => (
    <div className={`bg-white rounded-xl shadow-sm p-4 sm:p-5 ${className}`}>
        {(title || action) && (
            <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-800">{title}</h3>
                {action}
            </div>
        )}
        {children}
    </div>
);
